"use client"

import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "@/lib/router"
import { useTheme } from "next-themes"
import { useLocale } from "@/lib/i18n-client"
import {
  Calendar,
  CheckCircle2,
  Clock,
  FolderOpen,
  Home,
  LayoutDashboard,
  Monitor,
  Moon,
  Pause,
  Play,
  Settings,
  Square,
  Sun,
  TimerReset,
  Zap,
} from "lucide-react"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import { DialogTitle } from "@/components/ui/dialog"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { VisuallyHidden } from "@/components/ui/visually-hidden"
import { ScheduleTaskModal } from "@/components/modals/schedule-task-modal"
import { useCommandShortcutActions } from "@/hooks/use-command-shortcut-actions"
import {
  useComponentsTranslations,
  useNavigationTranslations,
  useTasksTranslations,
} from "@/hooks/use-translations"
import { useTaskStoreTranslations } from "@/lib/task-store-translations"
import { usePomodoroTimerStore } from "@/stores/pomodoro-timer-store"
import { usePersistentTimerStore } from "@/stores/persistent-timer-store"
import { usePreferencesStore, useTaskStore, useUserRoleStore } from "@/stores"
import { useCommandPaletteStore } from "@/stores/command-palette-store"
import { sanitize } from "@/utils/sanitize"
import { toast } from "sonner"
import type { LucideIcon } from "lucide-react"
import type { CommandItem as CommandItemType, TaskShortcutAction } from "@/stores/command-palette-store"
import type { Task } from "@/stores/task-store"
import { useCaptureStore } from "@/stores/capture-store"

type TaskPriority = "low" | "medium" | "high"

type NavCommandConfig = {
  id: string
  label: string
  icon: LucideIcon
  href: string
  keywords: string[]
}

type ActionCommandConfig = {
  id: string
  label: string
  icon: LucideIcon
  shortcut: string[]
  keywords: string[]
  visible: boolean
  onSelect: () => void
}

type ThemeCommandConfig = {
  id: string
  label: string
  icon: LucideIcon
  theme: "light" | "dark" | "system"
  keywords: string[]
}

const TASK_PICKER_TEXT: Record<TaskShortcutAction, string> = {
  "start-task-timer": "command_palette_pick_task_for_task_timer",
  "start-pomodoro-timer": "command_palette_pick_task_for_pomodoro",
  "mark-task-complete": "command_palette_pick_task_to_complete",
  "schedule-task": "command_palette_pick_task_to_schedule",
}

export function CommandPalette() {
  const {
    isOpen,
    setOpen,
    mode,
    setMode,
    commands: registeredCommands,
    pendingTaskAction,
    clearTaskAction,
  } = useCommandPaletteStore()
  const router = useRouter()
  const locale = useLocale()
  const { setTheme } = useTheme()
  const tasks = useTaskStore((state) => state.tasks)
  const scheduleTask = useTaskStore((state) => state.scheduleTask)
  const currentTaskTimer = usePersistentTimerStore((state) => state.currentTimer)
  const pomodoroIsRunning = usePomodoroTimerStore((state) => state.isRunning)
  const tNav = useNavigationTranslations()
  const tComp = useComponentsTranslations()
  const tTasks = useTasksTranslations()
  const { translations } = useTaskStoreTranslations()
  const {
    hasTaskTimer,
    hasPomodoro,
    openTaskActionShortcut,
    startTaskTimerForTask,
    pauseTaskTimer,
    endTaskTimer,
    startPomodoroForTask,
    pausePomodoroTimer,
    endPomodoroTimer,
    markTaskComplete,
  } = useCommandShortcutActions()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [scheduleTaskTarget, setScheduleTaskTarget] = useState<Task | null>(null)

  const isTaskTimerRunning = currentTaskTimer?.status === "running"
  const taskPickerText = pendingTaskAction ? tComp(TASK_PICKER_TEXT[pendingTaskAction]) : tComp("command_palette_placeholder")
  const taskCandidates = useMemo(
    () => (pendingTaskAction ? tasks.filter((task) => !task.archived && !task.completed) : []),
    [pendingTaskAction, tasks],
  )

  const navCommands = useMemo<CommandItemType[]>(() => {
    const navItems: NavCommandConfig[] = [
      { id: "nav-dashboard", label: tNav("dashboard"), icon: Home, href: "/", keywords: ["home"] },
      { id: "nav-tasks", label: tNav("tasks"), icon: LayoutDashboard, href: "/tasks", keywords: ["tasks"] },
      { id: "nav-calendar", label: tNav("calendar"), icon: Calendar, href: "/calendar", keywords: ["calendar"] },
      { id: "nav-timer", label: tNav("timer"), icon: Clock, href: "/timer", keywords: ["timer"] },
      { id: "nav-projects", label: tNav("projects"), icon: FolderOpen, href: "/projects", keywords: ["projects"] },
      { id: "nav-settings", label: tNav("settings"), icon: Settings, href: "/settings", keywords: ["settings"] },
    ]

    return navItems.map(({ href, ...item }) => ({
      ...item,
      group: tComp("command_palette_group_navigation"),
      onSelect: () => router.push(href),
    }))
  }, [router, tComp, tNav])

  const actionCommands = useMemo<CommandItemType[]>(() => {
    const actionItems: ActionCommandConfig[] = [
      {
        id: "action-brain-dump",
        label: tComp("brain_dump"),
        icon: Zap,
        shortcut: ["⌘", "B"],
        keywords: ["quick", "add", "task", "brain", "dump"],
        visible: true,
        onSelect: () => useCaptureStore.getState().open(),
      },
      {
        id: "action-start-task-timer",
        label: tComp("command_palette_start_task_timer"),
        icon: Play,
        shortcut: ["⌘", "⇧", "T"],
        keywords: ["task", "timer", "start", "focus"],
        visible: !hasTaskTimer && !hasPomodoro,
        onSelect: () => { openTaskActionShortcut("start-task-timer") },
      },
      {
        id: "action-pause-task-timer",
        label: tComp("command_palette_pause_task_timer"),
        icon: Pause,
        shortcut: ["⌘", "⌥", "T"],
        keywords: ["task", "timer", "pause"],
        visible: isTaskTimerRunning,
        onSelect: () => { void pauseTaskTimer() },
      },
      {
        id: "action-end-task-timer",
        label: tComp("command_palette_end_task_timer"),
        icon: Square,
        shortcut: ["⌘", "⇧", "⌥", "T"],
        keywords: ["task", "timer", "end", "stop"],
        visible: hasTaskTimer,
        onSelect: () => { void endTaskTimer() },
      },
      {
        id: "action-start-pomodoro-timer",
        label: tComp("command_palette_start_pomodoro_timer"),
        icon: Play,
        shortcut: ["⌘", "⇧", "P"],
        keywords: ["pomodoro", "focus", "start", "timer"],
        visible: !hasPomodoro && !hasTaskTimer,
        onSelect: () => { openTaskActionShortcut("start-pomodoro-timer") },
      },
      {
        id: "action-pause-pomodoro-timer",
        label: tComp("command_palette_pause_pomodoro_timer"),
        icon: Pause,
        shortcut: ["⌘", "⌥", "P"],
        keywords: ["pomodoro", "focus", "pause", "timer"],
        visible: pomodoroIsRunning,
        onSelect: () => { void pausePomodoroTimer() },
      },
      {
        id: "action-end-pomodoro-timer",
        label: tComp("command_palette_end_pomodoro_timer"),
        icon: TimerReset,
        shortcut: ["⌘", "⇧", "⌥", "P"],
        keywords: ["pomodoro", "focus", "end", "stop", "reset"],
        visible: hasPomodoro,
        onSelect: () => { void endPomodoroTimer() },
      },
      {
        id: "action-mark-task-complete",
        label: tComp("command_palette_mark_task_complete"),
        icon: CheckCircle2,
        shortcut: ["⌘", "⇧", "C"],
        keywords: ["task", "complete", "done", "finish"],
        visible: true,
        onSelect: () => { openTaskActionShortcut("mark-task-complete") },
      },
      {
        id: "action-schedule-task",
        label: tComp("command_palette_schedule_task"),
        icon: Calendar,
        shortcut: ["⌘", "⇧", "S"],
        keywords: ["task", "schedule", "calendar", "plan"],
        visible: true,
        onSelect: () => { openTaskActionShortcut("schedule-task") },
      },
    ]

    return actionItems
      .filter((item) => item.visible)
      .map((item) => ({
        id: item.id,
        label: item.label,
        icon: item.icon,
        shortcut: item.shortcut,
        keywords: item.keywords,
        onSelect: item.onSelect,
        group: tComp("command_palette_group_actions"),
      }))
  }, [
    endPomodoroTimer,
    endTaskTimer,
    hasPomodoro,
    hasTaskTimer,
    isTaskTimerRunning,
    openTaskActionShortcut,
    pausePomodoroTimer,
    pauseTaskTimer,
    pomodoroIsRunning,
    setMode,
    tComp,
  ])

  const themeCommands = useMemo<CommandItemType[]>(() => {
    const themeItems: ThemeCommandConfig[] = [
      { id: "theme-light", label: tComp("command_palette_light_mode"), icon: Sun, theme: "light", keywords: ["light", "theme"] },
      { id: "theme-dark", label: tComp("command_palette_dark_mode"), icon: Moon, theme: "dark", keywords: ["dark", "theme"] },
      { id: "theme-system", label: tComp("command_palette_system_theme"), icon: Monitor, theme: "system", keywords: ["system", "theme", "auto"] },
    ]

    return themeItems.map(({ theme, ...item }) => ({
      ...item,
      group: tComp("command_palette_group_theme"),
      onSelect: () => setTheme(theme),
    }))
  }, [setTheme, tComp])

  const groupedCommands = useMemo(() => {
    const groups = new Map<string, CommandItemType[]>()
    for (const command of [...navCommands, ...actionCommands, ...themeCommands, ...registeredCommands]) {
      if (!groups.has(command.group)) groups.set(command.group, [])
      groups.get(command.group)!.push(command)
    }
    return groups
  }, [actionCommands, navCommands, registeredCommands, themeCommands])

  const handleSelect = useCallback((command: CommandItemType) => {
    setOpen(false)
    requestAnimationFrame(() => {
      command.onSelect()
    })
  }, [setOpen])


  const handleTaskActionSelect = useCallback(async (task: Task) => {
    if (!pendingTaskAction) return

    if (pendingTaskAction === "schedule-task") {
      setScheduleTaskTarget(task)
      setOpen(false)
      clearTaskAction()
      return
    }

    setOpen(false)

    try {
      if (pendingTaskAction === "start-task-timer") {
        await startTaskTimerForTask(task)
      } else if (pendingTaskAction === "start-pomodoro-timer") {
        await startPomodoroForTask(task)
      } else if (pendingTaskAction === "mark-task-complete") {
        await markTaskComplete(task)
      }
    } catch {
      toast.error(tComp("command_palette_task_action_failed"))
    } finally {
      clearTaskAction()
    }
  }, [
    clearTaskAction,
    markTaskComplete,
    pendingTaskAction,
    setOpen,
    startPomodoroForTask,
    startTaskTimerForTask,
    tComp,
  ])

  const handleSchedule = useCallback(async (
    taskId: string,
    date: Date,
    recurrence?: {
      recurrence_enabled: boolean
      recurrence_type?: "daily" | "weekly" | "monthly"
      recurrence_interval?: number | null
      recurrence_weekdays?: number[] | null
      recurrence_anchor?: "scheduled" | "completion" | null
      recurrence_end_date?: string | null
      reminder_time?: number | null
    }
  ) => {
    const task = tasks.find((item) => item.id === taskId)
    if (!task) return

    try {
      await scheduleTask(taskId, date, translations, locale, undefined, recurrence)
      toast.success(tComp("command_palette_task_scheduled", { taskTitle: task.title }))
      setScheduleTaskTarget(null)
    } catch {
      toast.error(tComp("command_palette_task_action_failed"))
    }
  }, [locale, scheduleTask, tComp, tasks, translations])

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setOpen(open)
    if (!open) {
      clearTaskAction()
    }
  }, [clearTaskAction, setOpen])

  const dialogTitle =
    mode === "task-picker" ? taskPickerText : tComp("command_palette_placeholder")

  return (
    <>
      <CommandDialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <VisuallyHidden><DialogTitle>{dialogTitle}</DialogTitle></VisuallyHidden>
        {(
          <>
            <CommandInput placeholder={mode === "task-picker" ? taskPickerText : tComp("command_palette_placeholder")} />
            <CommandList>
              <CommandEmpty>
                {mode === "task-picker" ? tComp("command_palette_no_tasks_available") : tComp("command_palette_empty")}
              </CommandEmpty>
              {mode === "task-picker" ? (
                <CommandGroup heading={tComp("command_palette_group_tasks")}>
                  {taskCandidates.map((task) => (
                    <CommandItem
                      key={task.id}
                      value={task.title}
                      keywords={[task.description, task.priority, task.scheduled_date ?? "", task.scheduled_time ?? ""].filter(Boolean)}
                      onSelect={() => { void handleTaskActionSelect(task) }}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{task.title}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {task.scheduled_date
                            ? `${tComp("scheduled_date")}: ${task.scheduled_date}`
                            : tComp("command_palette_unscheduled_task")}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                Array.from(groupedCommands.entries()).map(([group, items], index) => (
                  <Fragment key={group}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup heading={group}>
                      {items.map((command) => (
                        <CommandItem
                          key={command.id}
                          value={command.label}
                          keywords={command.keywords}
                          onSelect={() => handleSelect(command)}
                        >
                          {command.icon && <command.icon className="mr-2 h-4 w-4" />}
                          <span>{command.label}</span>
                          {command.shortcut && <CommandShortcut>{command.shortcut.join("")}</CommandShortcut>}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Fragment>
                ))
              )}
            </CommandList>
          </>
        )}
      </CommandDialog>
      {scheduleTaskTarget && (
        <ScheduleTaskModal
          open={Boolean(scheduleTaskTarget)}
          onOpenChange={(open) => {
            if (!open) {
              setScheduleTaskTarget(null)
            }
          }}
          task={scheduleTaskTarget}
          onSchedule={handleSchedule}
        />
      )}
    </>
  )
}
